import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation } from 'react-router-dom';
import ScrollToTop from 'components/ScrollToTop';
import ErrorBoundary from 'components/ErrorBoundary';
import NotFound from 'pages/NotFound';
import { useAuth } from 'context/AuthContext';

// Resets the ErrorBoundary whenever the route changes — prevents one page's
// crash from freezing every subsequent page the user navigates to.
function LocationAwareErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

function PageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--color-primary)', borderTopColor: 'transparent' }} />
    </div>
  );
}

// App pages
const PortfolioOverview   = lazy(() => import('./pages/portfolio-overview'));
const UserDashboard       = lazy(() => import('./pages/user-dashboard'));
const AddHoldingPage      = lazy(() => import('./pages/add-holding-page'));
const AdminEmailPanel     = lazy(() => import('./pages/admin-email-panel'));
const AdminPlansPage      = lazy(() => import('./pages/admin-plans'));
const AdminPlanChanges    = lazy(() => import('./pages/admin-plan-changes'));
const AdminRevenue        = lazy(() => import('./pages/admin-revenue'));
const AdminUsersPage      = lazy(() => import('./pages/admin-users'));
const AdminDashboard      = lazy(() => import('./pages/admin-dashboard'));
const DividendCalendar    = lazy(() => import('./pages/dividend-calendar'));
const OptionsTracker      = lazy(() => import('./pages/options-tracker'));
const IncomeReports       = lazy(() => import('./pages/income-reports'));
const Settings            = lazy(() => import('./pages/settings'));
const EmailDeliveryTracker = lazy(() => import('./pages/email-delivery-tracker'));
const AlertsManagement    = lazy(() => import('./pages/alerts-management'));
const SubscriptionPage    = lazy(() => import('./pages/subscription'));

// Auth pages
const LoginPage          = lazy(() => import('./pages/login'));
const RegisterPage       = lazy(() => import('./pages/register'));
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password'));
const ResetPasswordPage  = lazy(() => import('./pages/reset-password'));
const VerifyEmailPage    = lazy(() => import('./pages/verify-email'));
const UnsubscribePage    = lazy(() => import('./pages/unsubscribe'));
const LandingPage        = lazy(() => import('./pages/landing'));
const BrokerageSync      = lazy(() => import('./pages/brokerage-sync'));
const StockDetailPage    = lazy(() => import('./pages/stock-detail'));
const WheelCostBasisPage = lazy(() => import('./pages/wheel-cost-basis'));

// Public info pages
const PricingPage          = lazy(() => import('./pages/pricing'));
const DRIPCalculatorPage       = lazy(() => import('./pages/drip-calculator'));
const CoveredCallTrackerPage      = lazy(() => import('./pages/covered-call-tracker'));
const VsSnowballAnalytics         = lazy(() => import('./pages/vs-snowball-analytics'));
const VsTrackYourDividends        = lazy(() => import('./pages/vs-track-your-dividends'));
const VsSimplySafeDividends       = lazy(() => import('./pages/vs-simply-safe-dividends'));
const GlossaryPage                = lazy(() => import('./pages/glossary'));
const PrivacyPolicyPage = lazy(() => import('./pages/privacy-policy'));
const TermsPage         = lazy(() => import('./pages/terms'));
const DeleteAccountPage = lazy(() => import('./pages/delete-account'));
const ContactPage       = lazy(() => import('./pages/contact'));
const BlogListPage      = lazy(() => import('./pages/blog'));
const BlogPostPage      = lazy(() => import('./pages/blog-post'));
const IdeasPage         = lazy(() => import('./pages/ideas'));
const NewsPage          = lazy(() => import('./pages/news'));
const WatchlistPage     = lazy(() => import('./pages/watchlist'));

// Admin blog
const AdminBlogList   = lazy(() => import('./pages/admin-blog'));
const AdminBlogEditor = lazy(() => import('./pages/admin-blog-editor'));

// Affiliate pages
const AffiliateLandingPage = lazy(() => import('./pages/affiliates'));
const AffiliateApplyPage   = lazy(() => import('./pages/affiliates/apply'));
const AffiliateDashboard   = lazy(() => import('./pages/affiliates/dashboard'));
const AdminAffiliatesPage  = lazy(() => import('./pages/admin-affiliates'));

// ── Route guards ───────────────────────────────────────────────────────────────

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--color-primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }
  if (!isAuthenticated) {
    // Store destination in sessionStorage — survives navigation, no React timing issues
    try { sessionStorage.setItem('after_login_dest', location.pathname + location.search); } catch {}
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  return children;
}

/** AdminRoute: must be authenticated AND have role === 'admin'. */
function AdminRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--color-primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== 'admin') {
    // Non-admin logged-in user → send to their dashboard
    return <Navigate to="/user-dashboard" replace />;
  }
  return children;
}

// ── Router ─────────────────────────────────────────────────────────────────────

const Routes = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <LocationAwareErrorBoundary>
        <Suspense fallback={<PageSpinner />}>
        <RouterRoutes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email"   element={<VerifyEmailPage />} />
          <Route path="/unsubscribe"    element={<UnsubscribePage />} />
          <Route path="/privacy-policy"  element={<PrivacyPolicyPage />} />
          <Route path="/privacy"         element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/terms"           element={<TermsPage />} />
          <Route path="/delete-account"  element={<DeleteAccountPage />} />
          <Route path="/contact"        element={<ContactPage />} />
          <Route path="/blog"           element={<BlogListPage />} />
          <Route path="/blog/:slug"     element={<BlogPostPage />} />
          <Route path="/affiliates"           element={<AffiliateLandingPage />} />
          <Route path="/affiliates/apply"     element={<AffiliateApplyPage />} />

          {/* Protected app routes */}
          <Route path="/user-dashboard"    element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
          <Route path="/portfolio-overview" element={<ProtectedRoute><PortfolioOverview /></ProtectedRoute>} />
          <Route path="/add-holding-page"  element={<ProtectedRoute><AddHoldingPage /></ProtectedRoute>} />
          <Route path="/dividend-calendar" element={<ProtectedRoute><DividendCalendar /></ProtectedRoute>} />
          <Route path="/options-tracker"   element={<ProtectedRoute><OptionsTracker /></ProtectedRoute>} />
          <Route path="/wheel-cost-basis/:ticker" element={<ProtectedRoute><WheelCostBasisPage /></ProtectedRoute>} />
          <Route path="/income-reports"    element={<ProtectedRoute><IncomeReports /></ProtectedRoute>} />
          <Route path="/settings"          element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/alerts-management" element={<ProtectedRoute><AlertsManagement /></ProtectedRoute>} />
          <Route path="/subscription"      element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          <Route path="/brokerage-sync"    element={<ProtectedRoute><BrokerageSync /></ProtectedRoute>} />
          <Route path="/stock/:ticker"      element={<ProtectedRoute><StockDetailPage /></ProtectedRoute>} />
          <Route path="/ideas"             element={<ProtectedRoute><IdeasPage /></ProtectedRoute>} />
          <Route path="/news"              element={<ProtectedRoute><NewsPage  /></ProtectedRoute>} />
          <Route path="/watchlist"         element={<ProtectedRoute><WatchlistPage /></ProtectedRoute>} />
          <Route path="/affiliates/dashboard" element={<ProtectedRoute><AffiliateDashboard /></ProtectedRoute>} />

          {/* Admin-only routes */}
          <Route path="/admin-blog"            element={<AdminRoute><AdminBlogList /></AdminRoute>} />
          <Route path="/admin-blog/new"        element={<AdminRoute><AdminBlogEditor /></AdminRoute>} />
          <Route path="/admin-blog/edit/:id"   element={<AdminRoute><AdminBlogEditor /></AdminRoute>} />
          <Route path="/admin"                 element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin-dashboard"       element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin-users"           element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/admin-email-panel"     element={<AdminRoute><AdminEmailPanel /></AdminRoute>} />
          <Route path="/admin-plan-changes"    element={<AdminRoute><AdminPlanChanges /></AdminRoute>} />
          <Route path="/admin-revenue"         element={<AdminRoute><AdminRevenue /></AdminRoute>} />
          <Route path="/admin-plans"           element={<AdminRoute><AdminPlansPage /></AdminRoute>} />
          <Route path="/email-delivery-tracker" element={<AdminRoute><EmailDeliveryTracker /></AdminRoute>} />
          <Route path="/admin-affiliates"      element={<AdminRoute><AdminAffiliatesPage /></AdminRoute>} />

          <Route path="/pricing"          element={<PricingPage />} />
          <Route path="/drip-calculator"      element={<DRIPCalculatorPage />} />
          <Route path="/covered-call-tracker"       element={<CoveredCallTrackerPage />} />
          <Route path="/vs-snowball-analytics"      element={<VsSnowballAnalytics />} />
          <Route path="/vs-track-your-dividends"    element={<VsTrackYourDividends />} />
          <Route path="/vs-simply-safe-dividends"   element={<VsSimplySafeDividends />} />
          <Route path="/glossary"                   element={<GlossaryPage />} />

          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
        </Suspense>
      </LocationAwareErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
